import {
  getApprovalSteps,
  getCurrentStepIndex,
} from "@/components/ProjectIntakeApprovalStatus/helpers";
import { PROJECT_STATUS, WORKFLOW_HISTORY_STATUS } from "@/enums";
import { flattenApprovers } from "@/helpers";
import { approvalWorkflowHistoryType } from "@/propTypes";
import {
  projectRoleApi,
  useGetApprovalWorkflowQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { useGetApprovalWorkflowIncludingDeletedQuery } from "@/services/approvalWorkflow";
import { FlexDiv } from "@/sharedStyled";
import { Card, Spin, Steps, Typography } from "antd";
import _ from "lodash";
import { arrayOf, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { ApprovalStepDescription } from "./components";

const { Step } = Steps;
const { Text } = Typography;

const DEFAULT_CURRENT_STEP = 0;

const ProjectIntakeApprovalStatus = ({
  approvalWorkflowId,
  workflowHistory,
  projectStatusId,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState();
  const [approvalSteps, setApprovalSteps] = useState();
  const [
    latestApprovedWorkflowHistoryEntry,
    setLatestApprovedWorkflowHistoryEntry,
  ] = useState();
  const [initialLoad, setInitialLoad] = useState(true);
  const [historyOfApprovals, setHistoryOfApprovals] = useState();
  const [projectRoles, setProjectRoles] = useState([]);

  const { data: approvalWorkflow } =
    useGetApprovalWorkflowQuery(approvalWorkflowId);
  const { data: approvalWorkflowIncludingDeleted } =
    useGetApprovalWorkflowIncludingDeletedQuery(approvalWorkflowId);
  const [getUsersByIds, { data: usersWhoApprovedSteps }] =
    useGetUsersByIdsMutation();

  useEffect(() => {
    if (!historyOfApprovals) return;

    const setByUserIds = historyOfApprovals?.map(({ setBy }) => setBy);
    const uniqueUserIds = new Set(setByUserIds);

    getUsersByIds([...uniqueUserIds]);
  }, [getUsersByIds, historyOfApprovals]);

  useEffect(() => {
    if (!historyOfApprovals?.length) return;

    setLatestApprovedWorkflowHistoryEntry(_.last(historyOfApprovals));
  }, [historyOfApprovals]);

  useEffect(() => {
    if (!approvalWorkflow?.approvers) return;

    const flattenApprovalWorkflow = flattenApprovers(
      approvalWorkflow.approvers
    );

    const approvalSteps = getApprovalSteps(
      projectStatusId,
      flattenApprovalWorkflow,
      usersWhoApprovedSteps,
      historyOfApprovals,
      approvalWorkflowIncludingDeleted
    );

    if (!approvalSteps) return;

    approvalSteps?.forEach(async ({ roleId }) => {
      const roleAlreadyFetched = projectRoles.some(({ id }) => id === roleId);

      if (roleAlreadyFetched) return;

      const { data } = await dispatch(
        projectRoleApi.endpoints.getProjectRole.initiate(roleId)
      );

      if (!data) return;

      setProjectRoles((prevState) => [...prevState, data]);
    });

    setApprovalSteps(approvalSteps);
  }, [
    approvalWorkflow?.approvers,
    usersWhoApprovedSteps,
    historyOfApprovals,
    dispatch,
    projectRoles,
    projectStatusId,
    approvalWorkflowIncludingDeleted,
  ]);

  useEffect(() => {
    if (!approvalSteps?.length) return;

    const latestCompletedStepId =
      latestApprovedWorkflowHistoryEntry?.workflowStepId;

    setCurrentStep(
      getCurrentStepIndex(approvalSteps, latestCompletedStepId) ||
        DEFAULT_CURRENT_STEP
    );
  }, [approvalSteps, latestApprovedWorkflowHistoryEntry]);

  useEffect(() => {
    if (!initialLoad) return;

    const workflowHistoryLoading =
      !usersWhoApprovedSteps && workflowHistory?.length;

    setInitialLoad(
      !approvalSteps ||
        currentStep === undefined ||
        workflowHistoryLoading ||
        !projectRoles.length
    );
  }, [
    approvalSteps,
    currentStep,
    initialLoad,
    projectRoles.length,
    usersWhoApprovedSteps,
    workflowHistory?.length,
  ]);

  useEffect(() => {
    if (!workflowHistory?.length) return;

    setHistoryOfApprovals(
      workflowHistory.filter(
        ({ currentStatus: status }) =>
          status === WORKFLOW_HISTORY_STATUS.approved
      )
    );
  }, [workflowHistory]);

  const isApproved =
    projectStatusId === PROJECT_STATUS.project.complete.id ||
    projectStatusId === PROJECT_STATUS.project.active.id;

  return (
    <Card className="w-full">
      <FlexDiv>
        <Text>
          {intl.formatMessage({
            id: "projectIntake.intakeApprovalStatus",
          })}
        </Text>
        {initialLoad ? (
          <Spin className="w-full self-center" />
        ) : (
          <Steps current={currentStep} status="process">
            {approvalSteps.map(
              ({
                id,
                roleId,
                userWhoApproved: user,
                persistedRoleName,
                formattedDate,
              }) => {
                const title =
                  persistedRoleName ??
                  projectRoles?.find(({ id }) => id === roleId)?.name;

                return (
                  <Step
                    key={id}
                    title={
                      title || (
                        <Text type="danger">
                          {intl.formatMessage({
                            id: "general.failedToRetrieve",
                          })}
                        </Text>
                      )
                    }
                    description={
                      !!(user && formattedDate) && (
                        <ApprovalStepDescription
                          user={user}
                          formattedDate={formattedDate}
                        />
                      )
                    }
                  />
                );
              }
            )}
            <Step
              title={intl.formatMessage({ id: "general.approved" })}
              status={isApproved ? "finish" : undefined}
            />
          </Steps>
        )}
      </FlexDiv>
    </Card>
  );
};

ProjectIntakeApprovalStatus.propTypes = {
  approvalWorkflowId: string.isRequired,
  workflowHistory: arrayOf(approvalWorkflowHistoryType),
  projectStatusId: string.isRequired,
};

export default ProjectIntakeApprovalStatus;
